import { memo, useCallback, useState } from 'react';
import cn from 'classnames';
import { useAsync } from 'react-use';
import { MATCH_STATUS } from '@zmedia/bm-soccer-ui/constants';
import { IMatch, ITeam } from '@zmedia/bm-soccer-ui/interface';
import { ObjectUtils } from '@zmedia/bm-soccer-ui/utils';
import { unixToDate } from '@zmedia/bm-soccer-ui/utils/date';
import { TournamentService } from '@zmedia/bm-soccer-ui/service/TournamentService';
import {
    Button,
    Element,
    Image,
    RouterLink,
    Section,
} from '@zmedia/bm-soccer-ui/components';
import { URL_SOCCER_PAGE } from '@zmedia/bm-soccer-ui/modules/shared';
import Empty from '@zmedia/bm-soccer-ui/modules/shared/components/Empty';
import styles from './style.module.scss';

interface IKnockout {
    branch: number;
    away: {
        score: number;
        team: ITeam;
    };
    home: {
        score: number;
        team: ITeam;
    };
    go: {
        matchId: number;
        date: number;
        match: IMatch;
    };
    back: {
        matchId: number;
        date: number;
        match: IMatch;
    };
    win?: ITeam;
}

const FALLBACK_TEAM =
    'https://baomoi-static.bmcdn.me/web/soccer/images/team_fallback.png';

export function TournamentKnockout(props: any) {
    const { id, seasonId } = props;
    let data = props.data || {};
    if (ObjectUtils.isEmpty(data)) {
        const state = useAsync(
            () =>
                TournamentService.getKnockout({
                    id: id,
                    seasonId: seasonId,
                }),
            [seasonId, id]
        );
        data = state.value?.data || {};
    }
    const items = data.items || [];
    let groups: any[][] = [];

    items.forEach((item: any) => {
        const _items = item.items;
        _items.forEach((item: any) => {
            const key: any = `${item.branch}`;
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(item);
        });
    });

    const [matchesSelect, selectMatches] = useState<IKnockout[]>([]);

    const renderColumn = (
        items: IKnockout[] = [],
        indexGroup: number,
        totalGroup: number
    ) => {
        const isLeft = indexGroup < totalGroup / 2;
        const isCenter = indexGroup === totalGroup / 2;
        const isRight = indexGroup > totalGroup / 2;
        const isFirstGroup = indexGroup === 1;
        const isLastGroup = indexGroup === totalGroup - 1;

        const showLineNext = items.length !== 1;

        return items.map((item: IKnockout, index: number) => {
            const isOdd = (index + 1) % 2 === 1;
            const isSelectMatch =
                matchesSelect.filter((match) => {
                    return (
                        match.home.team.id > 0 &&
                        match.away.team.id > 0 &&
                        match.home.team.id === item.home.team.id &&
                        match.away.team.id === item.away.team.id
                    );
                }).length > 0;
            const finalWin =
                item.home.score > item.away.score ? item.home : item.away;
            const isFullTime =
                item.go.match?.status?.id === MATCH_STATUS.FT ||
                !item.go.matchId;

            return (
                <Element
                    className={cn(styles['knockout-box'], {
                        [styles['is-select']]: isSelectMatch,
                    })}
                    key={index}
                >
                    {!isCenter && (
                        <>
                            {showLineNext ? (
                                <Element className={styles['line-wrapper']}>
                                    {!isFirstGroup && isLeft && (
                                        <Element
                                            className={cn(styles['line-back'])}
                                        />
                                    )}
                                    <Element
                                        className={cn(styles['line-top'], {
                                            [styles['is-up']]:
                                                isRight && !isOdd,
                                        })}
                                    ></Element>
                                    <Element
                                        className={cn(styles['line-bottom'], {
                                            [styles['is-down']]:
                                                isRight && isOdd,
                                        })}
                                    ></Element>
                                </Element>
                            ) : (
                                <>
                                    {!isFirstGroup && (
                                        <Element
                                            className={styles['line-wrapper']}
                                        >
                                            <Element
                                                className={cn(
                                                    styles['line-back']
                                                )}
                                            />
                                        </Element>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    <>
                        {item.go.matchId ? (
                            <RouterLink
                                to={`${URL_SOCCER_PAGE.SUBPATH}${item.go?.match?.path}`}
                            >
                                {renderMatch(item, isSelectMatch, isCenter)}
                            </RouterLink>
                        ) : (
                            <>{renderMatch(item, isSelectMatch, isCenter)}</>
                        )}
                        {isCenter && (
                            <Element className={styles['winner-team']}>
                                <Element className={styles['winner-logo']}>
                                    <Image
                                        src="https://baomoi-static.bmcdn.me/web/soccer/images/winner.svg"
                                        alt="winner"
                                    />
                                    <Image
                                        src={
                                            isFullTime ? finalWin.team.logo : ''
                                        }
                                        alt={
                                            isFullTime ? finalWin.team.name : ''
                                        }
                                    />
                                </Element>
                                <Element className={styles['winner-name']}>
                                    {isFullTime ? finalWin.team.name : ''}
                                </Element>
                                <Element>champion</Element>
                            </Element>
                        )}
                    </>
                    {!isCenter && (
                        <>
                            {showLineNext ? (
                                <Element
                                    className={cn(styles['line-wrapper'], {
                                        [styles['disable-hover']]:
                                            !item.go.matchId,
                                    })}
                                >
                                    <>
                                        {!isLastGroup && isRight && (
                                            <Element
                                                className={cn(
                                                    styles['line-back']
                                                )}
                                            />
                                        )}
                                        <Element
                                            className={cn(styles['line-top'], {
                                                [styles['is-up']]:
                                                    isLeft && !isOdd,
                                            })}
                                        ></Element>
                                        <Element
                                            className={cn(
                                                styles['line-bottom'],
                                                {
                                                    [styles['is-down']]:
                                                        isLeft && isOdd,
                                                }
                                            )}
                                        ></Element>
                                    </>
                                </Element>
                            ) : (
                                <>
                                    {!isLastGroup && (
                                        <Element
                                            className={styles['line-wrapper']}
                                        >
                                            <Element
                                                className={cn(
                                                    styles['line-back']
                                                )}
                                            />
                                        </Element>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Element>
            );
        });
    };

    const renderMatch = (
        item: IKnockout,
        isSelectMatch: boolean,
        isCenter: boolean
    ) => {
        const isFinalMatch = isCenter;
        const isNotStarted =
            item.go.match?.status?.id === MATCH_STATUS.NOT_STARTED ||
            !item.go.matchId;

        return (
            <>
                <Button
                    className={cn(styles['match-box'], {
                        [cn(styles['is-final'], '!min-h-[13.5rem]')]:
                            isFinalMatch,
                        [styles['is-select']]: isSelectMatch,
                    })}
                    onMouseEnter={() =>
                        selectMatch(item.home.team.id, item.away.team.id)
                    }
                    onMouseLeave={() => selectMatches([])}
                >
                    <Element className={styles['teams-logo']}>
                        <Image
                            src={item.home.team.logo || FALLBACK_TEAM}
                            alt={item.home.team.name || 'team'}
                        />
                        <Element>
                            {unixToDate(item.go.date / 1000, 'DD [thg] MM')}
                        </Element>
                        <Image
                            src={item.away.team.logo || FALLBACK_TEAM}
                            alt={item.away.team.name || 'team'}
                        />
                    </Element>
                    <Element className={styles['teams-info']}>
                        <Element
                            className={cn(styles['teams-name'], {
                                [styles['is-out']]:
                                    item.win &&
                                    item.home.team.id !== item.win.id,
                            })}
                        >
                            <Element className={'text-name'}>
                                {item.home.team.shortName}
                            </Element>
                            {!isNotStarted && (
                                <Element className={'text-score'}>
                                    {item.home.score}
                                </Element>
                            )}
                        </Element>
                        &nbsp; - &nbsp;
                        <Element
                            className={cn(styles['teams-name'], {
                                [styles['is-out']]:
                                    item.win &&
                                    item.away.team.id !== item.win.id,
                            })}
                        >
                            <Element className={'text-name'}>
                                {item.away.team.shortName}
                            </Element>
                            {!isNotStarted && (
                                <Element className={'text-score'}>
                                    {item.away.score}
                                </Element>
                            )}
                        </Element>
                    </Element>
                </Button>
            </>
        );
    };

    const selectMatch = (homeId: number, awayId: number) => {
        let _items: any = [];
        items.forEach((item: any) => {
            _items = [..._items, ...item.items];
        });

        if (!homeId && !awayId) {
            return;
        }

        const homeMatch = _items.filter((item: IKnockout) => {
            return item.home.team.id === homeId || item.away.team.id === homeId;
        });

        const awayMatch = _items.filter((item: IKnockout) => {
            return item.home.team.id === awayId || item.away.team.id === awayId;
        });

        selectMatches([...homeMatch, ...awayMatch]);
    };

    const renderColumns = useCallback(() => {
        return groups?.map((group, index) => {
            return (
                <Element
                    key={index}
                    className={cn(styles['knockout-round'])}
                    style={{
                        height: groups[1].length * 200 + 'px',
                    }}
                >
                    {renderColumn(Object.values(group), index, groups.length)}
                </Element>
            );
        });
    }, [groups]);

    if (!items.length) {
        return (
            <Section>
                <Empty content="Chưa có thông tin nào"></Empty>
            </Section>
        );
    }

    return (
        <Element className={items.length >= 4 ? 'knockout-box-vertical' : ''}>
            <Section className={styles['knockout-section']}>
                {renderColumns()}
            </Section>
        </Element>
    );
}

export default memo(TournamentKnockout);
